import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

// ─── lookup maps (static strings so Tailwind v4 scanner picks them all up) ────

const colClasses = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
} as const

const smColClasses = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
  12: 'sm:grid-cols-12',
} as const

const mdColClasses = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
  12: 'md:grid-cols-12',
} as const

// lg = DS Desktop (≥1440px after Phase 1 breakpoint override).
const lgColClasses = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  12: 'lg:grid-cols-12',
} as const

// xl = DS SuperWideScreen (≥1920px).
const xlColClasses = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
  12: 'xl:grid-cols-12',
} as const

// 2xl = DS UltraWideScreen (≥2560px).
const twoXlColClasses = {
  1: '2xl:grid-cols-1',
  2: '2xl:grid-cols-2',
  3: '2xl:grid-cols-3',
  4: '2xl:grid-cols-4',
  5: '2xl:grid-cols-5',
  6: '2xl:grid-cols-6',
  12: '2xl:grid-cols-12',
} as const

const gapClasses = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
  16: 'gap-16',
} as const

const colGapClasses = {
  0: 'gap-x-0',
  1: 'gap-x-1',
  2: 'gap-x-2',
  3: 'gap-x-3',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  8: 'gap-x-8',
  10: 'gap-x-10',
  12: 'gap-x-12',
  16: 'gap-x-16',
} as const

const rowGapClasses = {
  0: 'gap-y-0',
  1: 'gap-y-1',
  2: 'gap-y-2',
  3: 'gap-y-3',
  4: 'gap-y-4',
  5: 'gap-y-5',
  6: 'gap-y-6',
  8: 'gap-y-8',
  10: 'gap-y-10',
  12: 'gap-y-12',
  16: 'gap-y-16',
} as const

const spanClasses = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
  full: 'col-span-full',
} as const

// ─── Grid ─────────────────────────────────────────────────────────────────────

export namespace Grid {
  export type Cols = keyof typeof colClasses
  export type Gap = keyof typeof gapClasses
  export type Span = keyof typeof spanClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /** Number of columns (mobile-first base). */
    cols?: Cols
    /**
     * Responsive column count at DS Mobile Landscape / Tablet Portrait (sm ≥ 768px).
     * Was the old "show tablet layout" breakpoint — still the correct intent.
     */
    smCols?: Cols
    /**
     * Responsive column count at DS Desktop (md ≥ 1024px).
     * Use this for most "desktop layout" breakpoints — previously you would have
     * used `lgCols` when the Tailwind default `lg` was 1024px.
     */
    mdCols?: Cols
    /**
     * Responsive column count at DS WideScreen (lg ≥ 1440px).
     * Phase 1 shifted lg from 1024px → 1440px. Use `mdCols` for standard
     * desktop layouts; reserve `lgCols` for genuinely wide multi-pane layouts.
     */
    lgCols?: Cols
    /**
     * Responsive column count at DS SuperWideScreen (xl ≥ 1920px).
     */
    xlCols?: Cols
    /**
     * Responsive column count at DS UltraWideScreen (2xl ≥ 2560px).
     */
    twoXlCols?: Cols
    /** Gap between all cells. */
    gap?: Gap
    /** Column (horizontal) gap only. */
    colGap?: Gap
    /** Row (vertical) gap only. */
    rowGap?: Gap
  }
}

export function Grid({
  as: Comp = 'div',
  cols,
  smCols,
  mdCols,
  lgCols,
  xlCols,
  twoXlCols,
  gap,
  colGap,
  rowGap,
  className,
  children,
  ...props
}: Grid.Props) {
  return (
    <Comp
      className={cn(
        'grid',
        cols && colClasses[cols],
        smCols && smColClasses[smCols],
        mdCols && mdColClasses[mdCols],
        lgCols && lgColClasses[lgCols],
        xlCols && xlColClasses[xlCols],
        twoXlCols && twoXlColClasses[twoXlCols],
        gap !== undefined && gapClasses[gap],
        colGap !== undefined && colGapClasses[colGap],
        rowGap !== undefined && rowGapClasses[rowGap],
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── GridItem ─────────────────────────────────────────────────────────────────

export namespace GridItem {
  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /** Number of columns to span. */
    span?: Grid.Span
  }
}

export function GridItem({ as: Comp = 'div', span, className, children, ...props }: GridItem.Props) {
  return (
    <Comp className={cn(span && spanClasses[span], className)} {...props}>
      {children}
    </Comp>
  )
}
